/**
 * Inline approve/reject dialog for a single loan request.
 * Opened from NotificationCenter when a loan_request_received notification is clicked.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePendingRequestsForMe, useRespondToRequest } from '@/hooks/useLoanRequests';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface ApproveLoanRequestDialogProps {
  requestId: string | null;
  itemName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 *
 * @param root0
 * @param root0.requestId
 * @param root0.itemName
 * @param root0.open
 * @param root0.onOpenChange
 */
export function ApproveLoanRequestDialog({
  requestId,
  itemName,
  open,
  onOpenChange,
}: ApproveLoanRequestDialogProps) {
  const { data: requests } = usePendingRequestsForMe();
  const respondToRequest = useRespondToRequest();
  const { isOnline } = useNetworkStatusContext();

  const [hasDuration, setHasDuration] = useState(false);
  const [loanDuration, setLoanDuration] = useState('');
  const [loanDurationUnit, setLoanDurationUnit] = useState<'days' | 'weeks'>('days');
  const [loanNote, setLoanNote] = useState('');

  const request = requests?.find((r) => r.id === requestId);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setHasDuration(false);
      setLoanDuration('');
      setLoanDurationUnit('days');
      setLoanNote('');
    }
    onOpenChange(open);
  };

  const handleRespond = async (approved: boolean) => {
    if (!requestId) return;
    let dueAt: string | undefined;
    if (approved && hasDuration && loanDuration) {
      const durationNum = parseInt(loanDuration, 10);
      if (Number.isNaN(durationNum) || durationNum < 1) {
        toast.error('Please enter a valid duration (at least 1 day).');
        return;
      }
      const durationDays = loanDurationUnit === 'weeks' ? durationNum * 7 : durationNum;
      const due = new Date();
      due.setDate(due.getDate() + durationDays);
      dueAt = due.toISOString();
    }
    try {
      await respondToRequest.mutateAsync({ requestId, approved, dueAt, note: loanNote.trim() || undefined });
      toast.success(approved ? 'Request approved!' : 'Request rejected');
      handleOpenChange(false);
    } catch {
      // Error toast handled by useRespondToRequest onError
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {!request ? (
          <>
            <DialogHeader>
              <DialogTitle>Loan Request</DialogTitle>
            </DialogHeader>
            <p style={{ fontSize: 13, color: T.textMuted, padding: '8px 0' }}>
              This request has already been processed.
            </p>
            <DialogFooter>
              <button
                onClick={() => handleOpenChange(false)}
                style={{
                  padding: '10px 18px',
                  borderRadius: RADIUS.md,
                  background: T.surfaceSubtle,
                  color: T.textSec,
                  border: `1px solid ${T.border}`,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Close
              </button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {request.requester_name} wants to borrow {request.item_name ?? itemName}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              {request.message && (
                <p className="text-sm text-muted-foreground">"{request.message}"</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
              </p>
            </div>
            <div className="space-y-3">
              <Label>Set a loan duration?</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="approveHasDuration"
                    checked={!hasDuration}
                    onChange={() => {
                      setHasDuration(false);
                      setLoanDuration('');
                    }}
                    className="accent-primary"
                  />
                  <span className="text-sm">No — Unlimited</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="approveHasDuration"
                    checked={hasDuration}
                    onChange={() => setHasDuration(true)}
                    className="accent-primary"
                  />
                  <span className="text-sm">Yes — Set deadline</span>
                </label>
              </div>
              {hasDuration && (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g., 7"
                    value={loanDuration}
                    onChange={(e) => setLoanDuration(e.target.value)}
                    className="flex-1"
                  />
                  <Select
                    value={loanDurationUnit}
                    onValueChange={(v) => setLoanDurationUnit(v as 'days' | 'weeks')}
                  >
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Note for borrower (optional)</Label>
              <textarea
                value={loanNote}
                onChange={(e) => setLoanNote(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="e.g. Needs AA batteries, blade guard is loose..."
                style={{
                  width: '100%',
                  background: T.surfaceSubtle,
                  border: `1px solid ${T.border}`,
                  borderRadius: RADIUS.md,
                  padding: '10px 12px',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  minHeight: 56,
                  outline: 'none',
                  color: T.text,
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = T.accent; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = T.border; }}
              />
              {loanNote.length > 0 && (
                <span style={{ fontSize: 12, color: T.textMuted }}>
                  {loanNote.length}/500
                </span>
              )}
            </div>
            <DialogFooter className="gap-2">
              <button
                onClick={() => handleRespond(false)}
                disabled={respondToRequest.isPending || !isOnline}
                title={!isOnline ? "You're offline" : undefined}
                style={{
                  padding: '10px 18px',
                  borderRadius: RADIUS.md,
                  background: T.surfaceSubtle,
                  color: T.textSec,
                  border: `1px solid ${T.border}`,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: respondToRequest.isPending || !isOnline ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: respondToRequest.isPending || !isOnline ? 0.6 : 1,
                }}
              >
                Reject
              </button>
              <button
                onClick={() => handleRespond(true)}
                disabled={respondToRequest.isPending || !isOnline}
                title={!isOnline ? "You're offline" : undefined}
                style={{
                  padding: '10px 18px',
                  borderRadius: RADIUS.md,
                  background: respondToRequest.isPending || !isOnline ? T.surfaceSubtle : T.accent,
                  color: respondToRequest.isPending || !isOnline ? T.textMuted : T.white,
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: respondToRequest.isPending || !isOnline ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: respondToRequest.isPending || !isOnline ? 'none' : SHADOW.btn.accent,
                }}
              >
                Approve
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
